import React, {
  Suspense, lazy, useCallback, useEffect, useState,
} from 'react';
import api from '../../ui/api';
import { INTRO_VERSION, compareVersions } from './version';
import { enterFullscreen, leaveFullscreen } from './fullscreen';
import './introGate.css';

/**
 * What «بوابة مشكاة» shows when the app opens:
 *   never seen the welcome   → 'welcome' — the full intro
 *   seen an older version    → 'update'  — «ما الجديد»
 *   otherwise, once a day    → 'tips'    — the next station of the tour
 *                               «اكتشف بوابة مشكاة» (Tips.js), until the
 *                               person has seen them all or switched it off
 *
 * The account's record on the server wins over this browser's, so a
 * veteran on a new computer is not welcomed again, and the tour carries
 * on where it stopped on any device. While the server is asked for the
 * welcome, the intro's night sky already covers the screen; the daily
 * tour is asked quietly, with the app visible. Offline or slow → the
 * welcome plays anyway after a few seconds.
 */
const key = (uid) => `mtz.introSeen.${uid}`;
const tipsKey = (uid) => `mtz.introTips.${uid}`;
const readLocal = (uid) => { try { return localStorage.getItem(key(uid)); } catch { return null; } };
const writeLocal = (uid, v) => { try { localStorage.setItem(key(uid), v); } catch { /* private window */ } };
const isCurrent = (v) => !!v && compareVersions(v, INTRO_VERSION) >= 0;

const NO_TIPS = {
  done: [], last: null, off: false, complete: false,
};
const readTips = (uid) => {
  try { return { ...NO_TIPS, ...JSON.parse(localStorage.getItem(tipsKey(uid)) || '{}') }; } catch { return { ...NO_TIPS }; }
};
const writeTips = (uid, t) => { try { localStorage.setItem(tipsKey(uid), JSON.stringify(t)); } catch { /* private window */ } };
const mergeTips = (a, b) => (b ? {
  done: [...new Set([...(a.done || []), ...(b.done || [])])],
  last: [a.last, b.last].filter(Boolean).sort().pop() || null,
  off: !!(a.off || b.off),
  complete: !!(a.complete || b.complete),
} : a);
/** This device's calendar day — "once a day" means the person's day. */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const tipsDue = (t) => !t.off && !t.complete && t.last !== today();

export function useIntroGate(user) {
  const uid = user?.id;
  const [state, setState] = useState(() => {
    if (!isCurrent(readLocal(uid))) return { phase: 'checking' };
    return tipsDue(readTips(uid)) ? { phase: 'checking', quiet: true } : null;
  });

  useEffect(() => {
    if (state?.phase !== 'checking') return undefined;
    let settled = false;
    const decide = (server) => {
      if (settled) return;
      settled = true;
      const seen = [readLocal(uid), server?.seen_version].filter(Boolean).sort(compareVersions).pop() || null;
      const tips = mergeTips(readTips(uid), server?.tips);
      writeTips(uid, tips);
      if (!isCurrent(seen)) {
        // Still within the login click's few seconds: full screen now.
        enterFullscreen(true);
        setState({ phase: 'show', mode: seen ? 'update' : 'welcome', seen });
        return;
      }
      writeLocal(uid, seen);
      setState(tipsDue(tips) ? { phase: 'tips' } : null);
    };
    const timer = setTimeout(() => decide(null), 3000);
    api.introStatus().then(decide, () => decide(null));
    return () => { settled = true; clearTimeout(timer); };
  }, [state, uid]);

  // The welcome / «ما الجديد» ended. That counts as today's look at the
  // system: the tour starts tomorrow.
  const finish = useCallback(() => {
    leaveFullscreen();
    writeLocal(uid, INTRO_VERSION);
    const tips = { ...readTips(uid), last: today() };
    writeTips(uid, tips);
    api.saveIntro({ version: INTRO_VERSION, tips }).catch(() => { /* this browser remembers it anyway */ });
    setState(null);
  }, [uid]);

  /** A tour station closed: { done, complete } «فهمت» · { later } «ذكّرني غداً» · { off } «إيقاف الجولة». */
  const finishTips = useCallback((result = {}) => {
    const t = readTips(uid);
    const tips = {
      ...t,
      last: today(),
      done: result.done && !t.done.includes(result.done) ? [...t.done, result.done] : t.done,
      off: t.off || !!result.off,
      complete: t.complete || !!result.complete,
    };
    writeTips(uid, tips);
    api.saveIntro({ tips }).catch(() => { /* this browser remembers it anyway */ });
    setState(null);
  }, [uid]);

  // The intro could not load (offline right after a deploy): let the
  // person work, and greet them next time.
  const dismiss = useCallback(() => {
    leaveFullscreen();
    setState(null);
  }, []);

  // «جولة الترحيب» (✨).
  const replay = useCallback(() => {
    enterFullscreen(true);
    setState({
      phase: 'show', mode: 'welcome', seen: null, replay: true,
    });
  }, []);

  // The tour button in the top bar: browse every station, any time. Only
  // looking — it changes nothing in the daily tour.
  const openTour = useCallback(() => setState({ phase: 'tips', browse: true }), []);
  const closeTour = useCallback(() => setState(null), []);

  return {
    state, finish, finishTips, dismiss, replay, openTour, closeTour,
  };
}

// The intro itself (animations, sounds, canvas) and the tour are their
// own downloads, fetched only when someone is actually shown one.
const Intro = lazy(() => import('./Intro').catch(() => ({ default: Unavailable })));
const Tips = lazy(() => import('./Tips').catch(() => ({ default: Unavailable })));

function Unavailable({ onUnavailable }) {
  useEffect(() => { onUnavailable(); }, [onUnavailable]);
  return null;
}

export function IntroLayer({ gate, user }) {
  const {
    state, finish, finishTips, dismiss, closeTour,
  } = gate;
  if (!state) return null;
  const curtain = <div className="intro-curtain" aria-hidden="true" />;
  if (state.phase === 'checking') return state.quiet ? null : curtain;
  if (state.phase === 'tips') {
    return (
      <Suspense fallback={null}>
        <Tips
          user={user}
          browse={!!state.browse}
          onDone={state.browse ? closeTour : finishTips}
          onUnavailable={dismiss}
        />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={curtain}>
      <Intro
        mode={state.mode}
        seen={state.seen}
        replay={!!state.replay}
        user={user}
        onDone={finish}
        onUnavailable={dismiss}
      />
    </Suspense>
  );
}
